import { useDebouncedState, useDocumentTitle } from '@mantine/hooks'
import { ReactComponent as ActiveConversationsIcon } from '@shared/assets/icons/ic-active-conversation-color.svg'
import { useKeyboardShortcuts } from '@shared/hooks/use-keyboard-shortcuts'
import { useCommandRegister } from 'cmdk-engine/react'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { getRouteTitle } from 'v2/configs/routes'
import { useAccountDetailsQuery } from 'v2/pages/billing/queries'
import { useSenderIdAccessQuery } from 'v2/pages/settings/phone-numbers/sender-id-queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import {
  type AppCommandItem,
  toKeyboardShortcuts,
  withDisplayShortcuts,
} from 'v2/shared/utils/commands/types'
import { ConvChannels } from 'v2/types/api/conversations'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getLocalAuth, getSessionRoles } from 'v2/utils'
import { useChannelLocks } from '../hooks/use-channel-locks'
import { useDarkMode } from '../hooks/use-dark-mode'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import { usePermissions } from '../hooks/use-permissions'
import { useUserPlan } from '../hooks/use-plan'
import { getTabbedPageCommands } from '../utils/commands/tabbed-page-commands'
import {
  canViewBuildSection,
  canViewCompliance,
  canViewLogs,
  canViewMonitorSection,
  isSupportOnly,
  isUccReviewerOnly,
} from '../utils/role-based-visibility'
import { ChannelIcon } from './channel-icons'

/**
 * Component that handles location-based effects: document title, the always-on "Global"
 * command group (dark mode, active conversations, start-new-conversation), and
 * route-change side effects.
 *
 * Settings sub-pages no longer need a dynamic command swap here — they're permanent, always-
 * searchable leaves via the route-driven command palette (routes-to-commands.ts), just marked
 * `hidden` so they stay out of the idle browse list.
 *
 * @returns null
 */
export const LocationEffectHandler = () => {
  const location = useLocation()
  const navigateTo = useNavigate()
  const [isDarkMode, toggleDarkMode] = useDarkMode()
  const [title, setTitle] = useDebouncedState('Plivo', 50)
  const { data: profileData } = useProfileQuery()
  const org = profileData?.organizations?.[0]
  const humanSpecialistEnabled = org?.human_specialist_enabled ?? false
  // Per-type submission flags (10DLC / Toll-free): mirrors CompliancePage's
  // hide10dlc/hideTollFree exactly — only an explicit `false` hides the tab.
  const tendlcEnabled = org?.tendlc_submission_enabled !== false
  const tollfreeEnabled = org?.tollfree_submission_enabled !== false
  const { hasPermission, permissions } = usePermissions()
  const { usageReportsEnabled } = useFeatureFlags()
  const localAuth = getLocalAuth()
  const isIndianRegion = localAuth.region === REGIONS.IN
  const isUsRegion = localAuth.region === REGIONS.US
  // Support / UCC-reviewer sessions are locked to the UCC tab (phone-numbers-combined
  // wraps every other tab in !isUccOnlyUser) — so they must not be offered Number
  // Compliance even if their permission map includes channel view.
  const roles = getSessionRoles()
  const isUccOnlyUser = isSupportOnly({ roles }) || isUccReviewerOnly({ roles })
  const canViewBuild = canViewBuildSection({ roles })
  const canViewMonitor = canViewMonitorSection({ roles })
  const canViewLogsTabs = canViewLogs({ roles, permissionsMap: permissions })
  const canViewComplianceTabs = canViewCompliance({ isUsRegion, permissionsMap: permissions })
  const { hasSenderIdAccess = false } = useSenderIdAccessQuery()
  const { data: accountDetails } = useAccountDetailsQuery()
  const isNonPostpaidAccount = accountDetails?.account?.postpaid === false
  // Enterprise plan lock — keeps the messaging-channel tab commands out of the palette for
  // plans whose hubs would only ghost them and bounce back to voice. isPlanLoaded matters as
  // much as the lock: the lock is false until the profile resolves, so without it these
  // commands are registered on first paint and a quick cmd+K selection still reaches the
  // locked deep link.
  const { isEnterpriseLocked, isPlanLoaded } = useUserPlan()
  const { isWhatsappLocked, isVerifyLocked, isVerifyAvailable, isChannelLocksLoaded } =
    useChannelLocks()

  useDocumentTitle(title || 'CX')

  const globalCommands = useMemo<AppCommandItem[]>(
    () =>
      withDisplayShortcuts([
        {
          id: 'global-toggle-dark-mode',
          label: `Toggle Dark Mode ${isDarkMode ? 'Off' : 'On'}`,
          group: 'Global',
          action: () => toggleDarkMode(),
          realShortcut: { key: 'd', meta: true, shift: false },
          onlyOnHover: true,
        },
        ...(humanSpecialistEnabled
          ? [
              {
                id: 'global-active-conversations',
                label: 'Go to Active Conversations',
                icon: <ActiveConversationsIcon className="w-4 h-4" />,
                group: 'Global',
                action: () => navigateTo('/conversations/active'),
                realShortcut: { key: '1', meta: true, shift: false },
                onlyOnHover: true,
              },
            ]
          : []),
        // Channel-scoped "start new conversation" commands — only offered for
        // channels the account actually has enabled (same gate as the Platform
        // nav routes). Previously hardcoded/always-shown.
        ...(hasPermission(RbacModules.channelWhatsapp, RbacPermissionTypes.view)
          ? [
              {
                id: 'global-new-whatsapp',
                label: 'Start a new WhatsApp conversation',
                icon: <ChannelIcon channel={ConvChannels.WHATSAPP} className="w-4 h-4" />,
                group: 'Global',
                action: () => navigateTo('/conversations/new/whatsapp'),
              },
            ]
          : []),
        ...(hasPermission(RbacModules.channelMessage, RbacPermissionTypes.view)
          ? [
              {
                id: 'global-new-sms',
                label: 'Start a new SMS conversation',
                icon: <ChannelIcon channel={ConvChannels.MESSAGE} className="w-4 h-4" />,
                group: 'Global',
                action: () => navigateTo('/conversations/new/message'),
              },
            ]
          : []),
        ...(hasPermission(RbacModules.channelCalls, RbacPermissionTypes.view)
          ? [
              {
                id: 'global-new-call',
                label: 'Start a new Call conversation',
                icon: <ChannelIcon channel={ConvChannels.CALL} className="w-4 h-4" />,
                group: 'Global',
                action: () => navigateTo('/conversations/new/call'),
              },
            ]
          : []),
        // Tabbed-page sub-routes the route-derived palette can't emit (dynamic
        // `/:tab`/`:direction?` catch-alls get skipped). See getTabbedPageCommands
        // for the per-destination gates — incl. withholding Number Compliance from
        // UCC-only sessions, which can't reach that tab.
        ...getTabbedPageCommands({
          isIndianRegion,
          isUccOnlyUser,
          hasUccView: hasPermission(RbacModules.ucc, RbacPermissionTypes.view),
          hasNumbersAccess:
            hasPermission(RbacModules.channelCalls, RbacPermissionTypes.view) ||
            hasPermission(RbacModules.channelMessage, RbacPermissionTypes.view),
          hasReportView: hasPermission(RbacModules.report, RbacPermissionTypes.view),
          hasAgentView: hasPermission(RbacModules.aiAgent, RbacPermissionTypes.view),
          usageReportsEnabled,
          isUsRegion,
          hasSenderIdAccess,
          tendlcEnabled,
          tollfreeEnabled,
          canViewComplianceTabs,
          canViewMonitor,
          isNonPostpaidAccount,
          canViewLogsTabs,
          canViewBuild,
          isEnterpriseLocked,
          isWhatsappLocked,
          isVerifyLocked,
          isVerifyAvailable,
          isChannelLocksLoaded,
          isPlanLoaded,
          navigateTo,
        }),
      ]),
    [
      isDarkMode,
      humanSpecialistEnabled,
      toggleDarkMode,
      navigateTo,
      hasPermission,
      isIndianRegion,
      isUccOnlyUser,
      usageReportsEnabled,
      isUsRegion,
      hasSenderIdAccess,
      tendlcEnabled,
      tollfreeEnabled,
      canViewComplianceTabs,
      canViewMonitor,
      isNonPostpaidAccount,
      canViewLogsTabs,
      canViewBuild,
      isEnterpriseLocked,
      isWhatsappLocked,
      isVerifyLocked,
      isVerifyAvailable,
      isChannelLocksLoaded,
      isPlanLoaded,
    ],
  )

  useCommandRegister(globalCommands, [globalCommands])
  useKeyboardShortcuts(useMemo(() => toKeyboardShortcuts(globalCommands), [globalCommands]))

  useEffect(() => {
    const routeTitle = getRouteTitle(location.pathname)
    setTitle(`${routeTitle || 'CX'} | Plivo`)
  }, [location.pathname])

  return null
}
